import type { ArtifactType } from '../../containers/flora/flora.type';
import type { QuickLink } from './flora-quick-links.type';

const APPLICATION_BASE = '/application';

interface BoardEntry {
  id: string;
  name: string;
  work_period_ids?: string[];
}

interface WorkPeriodEntry {
  id?: string;
  start_date?: string;
  end_date?: string;
}

/**
 * Filters an unknown value down to a string list.
 *
 * @param {unknown} value - Potential array value from artifact config.
 * @returns {string[]} String entries from the array, or an empty array for invalid values.
 */
function getStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
}

/**
 * Reads valid board entries from artifact config while normalizing optional work period ids.
 *
 * @param {Record<string, unknown> | undefined} config - Artifact config that may contain a boards array.
 * @returns {BoardEntry[]} Valid board entries with safe work_period_ids arrays.
 */
function getBoardEntries(config: Record<string, unknown> | undefined): BoardEntry[] {
  const boards = config?.boards;
  if (!Array.isArray(boards)) return [];

  return boards.flatMap((board) => {
    if (!board || typeof board !== 'object') return [];

    const boardRecord = board as Record<string, unknown>;
    if (typeof boardRecord.id !== 'string' || typeof boardRecord.name !== 'string') return [];

    return [
      {
        id: boardRecord.id,
        name: boardRecord.name,
        work_period_ids: getStringList(boardRecord.work_period_ids),
      },
    ];
  });
}

/**
 * Extracts the first work period entry from a data object containing a `work_periods` array.
 *
 * @param {Record<string, unknown>} data - The data object potentially containing a `work_periods` array.
 * @returns {WorkPeriodEntry | null} The first valid WorkPeriodEntry object, or null if none found.
 */
function getFirstWorkPeriod(data: Record<string, unknown>): WorkPeriodEntry | null {
  const workPeriods = data.work_periods as unknown;
  if (!Array.isArray(workPeriods) || workPeriods.length === 0) return null;

  const [first] = workPeriods;
  if (!first || typeof first !== 'object') return null;

  return first as WorkPeriodEntry;
}

/**
 * Builds a quick link URL for the Process module from a process_map_chart artifact.
 *
 * @param {Record<string, unknown>} _data - The artifact data payload
 * @param {Record<string, unknown>} config - The artifact config containing team_id, board_id, dates, etc.
 * @returns {QuickLink[]} Array of quick links (0 or 1 element)
 */
function buildProcessQuickLink(
  _data: Record<string, unknown>,
  config: Record<string, unknown> | undefined,
): QuickLink[] {
  if (!config) return [];

  const teamId = config.team_id as string | undefined;

  // team_id is the minimum required context for the process module
  if (!teamId) return [];

  const params = new URLSearchParams();
  params.set('teamId', teamId);

  const boardId = config.board_id as string | undefined;
  if (boardId) {
    params.set('boardId', boardId);
  }

  const startDate = config.start_date as string | undefined;
  if (startDate) {
    params.set('startDate', startDate);
  }

  const endDate = config.end_date as string | undefined;
  if (endDate) {
    params.set('endDate', endDate);
  }

  const useGroups = config.use_groups;
  if (typeof useGroups === 'boolean') {
    params.set('useGroups', String(useGroups));
  }

  const workPeriodIds = getStringList(config.work_period_ids);
  if (workPeriodIds.length > 0) {
    params.set('workPeriodIds', workPeriodIds.join(','));
  }

  return [
    {
      url: `${APPLICATION_BASE}/process?${params.toString()}`,
      label: 'Open in Process',
    },
  ];
}

/**
 * Determines if a measure comparison artifact is sprint-level based on the config.
 *
 * @param {Record<string, unknown>} config - The artifact config
 * @returns {boolean} True if the artifact represents sprint-level data
 */
function isSprintLevel(config: Record<string, unknown> | undefined): boolean {
  if (!config) return false;
  return config.scope_level === 'sprint' || config.object_type === 'work_period';
}

/**
 * Builds a quick link URL for the Process Analysis module from a non-sprint measure_comparison_chart artifact.
 *
 * @param {Record<string, unknown>} config - The artifact config
 * @returns {QuickLink | null} The quick link or null if insufficient context
 */
function buildProcessAnalysisQuickLink(config: Record<string, unknown> | undefined): QuickLink | null {
  if (!config) return null;

  const params = new URLSearchParams();
  let hasContext = false;

  const startDate = config.start_date as string | undefined;
  const endDate = config.end_date as string | undefined;
  if (startDate && endDate) {
    params.set('startDate', startDate);
    params.set('endDate', endDate);
    hasContext = true;
  }

  const portfolioIds = config.portfolio_ids as string[] | undefined;
  if (portfolioIds && portfolioIds.length > 0) {
    params.set('portfolioIds', portfolioIds.join(','));
    params.set('activeTab', 'portfolios');
    hasContext = true;
  }

  const teamIds = config.team_ids as string[] | undefined;
  if (teamIds && teamIds.length > 0) {
    params.set('teamIds', teamIds.join(','));
    params.set('activeTab', 'teams');
    hasContext = true;
  }

  const boardIds = config.board_ids as string[] | undefined;
  if (boardIds && boardIds.length > 0) {
    params.set('boardIds', boardIds.join(','));
    params.set('activeTab', 'boards');
    hasContext = true;
  }

  const selectedMeasures = config.selected_measures as string[] | undefined;
  if (selectedMeasures && selectedMeasures.length > 0) {
    params.set('selectedMeasures', selectedMeasures.join(','));
  }

  if (!hasContext) return null;

  return {
    url: `${APPLICATION_BASE}/process-analysis?${params.toString()}`,
    label: 'Open in Process Analysis',
  };
}

/**
 * Builds quick links for sprint-level measure comparison charts.
 * When multiple boards are present, returns one link per board.
 *
 * @param {Record<string, unknown>} config - The artifact config containing boards array
 * @returns {QuickLink[]} Array of quick links (one per board, or empty)
 */
function buildSprintMeasureComparisonQuickLinks(config: Record<string, unknown> | undefined): QuickLink[] {
  if (!config) return [];

  const boards = getBoardEntries(config);
  if (boards.length === 0) return [];

  const startDate = config.start_date as string | undefined;
  const endDate = config.end_date as string | undefined;
  const selectedMeasures = config.selected_measures as string[] | undefined;
  const isSingleBoard = boards.length === 1;

  return boards.map((board) => {
    const params = new URLSearchParams();
    params.set('workPeriodBoardId', board.id);
    params.set('selectedTab', 'comparison_measure_comparison');

    if (startDate) {
      params.set('startDate', startDate);
    }
    if (endDate) {
      params.set('endDate', endDate);
    }
    if (board.work_period_ids && board.work_period_ids.length > 0) {
      params.set('selectedWorkPeriodIds', board.work_period_ids.join(','));
    }
    if (selectedMeasures && selectedMeasures.length > 0) {
      params.set('selectedMeasures', selectedMeasures.join(','));
    }

    return {
      key: board.id,
      url: `${APPLICATION_BASE}/work-periods?${params.toString()}`,
      label: isSingleBoard ? 'Open in Work Periods' : `Open '${board.name}' in Work Periods`,
    };
  });
}

/**
 * Builds quick links for the Measure Comparison artifact.
 * Routes to Process Analysis for non-sprint data, or Work Periods for sprint-level data.
 *
 * @param {Record<string, unknown>} _data - The artifact data payload
 * @param {Record<string, unknown>} config - The artifact config
 * @returns {QuickLink[]} Array of quick links
 */
function buildMeasureComparisonQuickLink(
  _data: Record<string, unknown>,
  config: Record<string, unknown> | undefined,
): QuickLink[] {
  if (isSprintLevel(config)) {
    return buildSprintMeasureComparisonQuickLinks(config);
  }

  const link = buildProcessAnalysisQuickLink(config);
  return link ? [link] : [];
}

/**
 * Builds quick links for the Work Periods module from a measures_over_time_chart artifact.
 * Deep-links into the sprint comparison "measures over time" tab.
 * When multiple boards are present, returns one link per board.
 *
 * @param {Record<string, unknown>} _data - The artifact data payload containing work_periods array
 * @param {Record<string, unknown>} config - The artifact config containing boards array
 * @returns {QuickLink[]} Array of quick links (one per board, or empty)
 */
function buildMeasuresOverTimeQuickLink(
  _data: Record<string, unknown>,
  config: Record<string, unknown> | undefined,
): QuickLink[] {
  const boards = getBoardEntries(config);
  if (boards.length === 0) return [];

  const startDate = config?.start_date as string | undefined;
  const endDate = config?.end_date as string | undefined;
  const selectedMeasures = getStringList(config?.selected_measures);
  const isSingleBoard = boards.length === 1;

  return boards.map((board) => {
    const params = new URLSearchParams();
    params.set('workPeriodBoardId', board.id);
    params.set('selectedTab', 'comparison_measures_over_time');

    if (startDate) {
      params.set('startDate', startDate);
    }
    if (endDate) {
      params.set('endDate', endDate);
    }
    if (board.work_period_ids && board.work_period_ids.length > 0) {
      params.set('selectedWorkPeriodIds', board.work_period_ids.join(','));
    }
    if (selectedMeasures.length > 0) {
      params.set('selectedMeasures', selectedMeasures.join(','));
    }

    return {
      key: board.id,
      url: `${APPLICATION_BASE}/work-periods?${params.toString()}`,
      label: isSingleBoard ? 'Open in Work Periods' : `Open '${board.name}' in Work Periods`,
    };
  });
}

/**
 * Builds quick links for the Work Periods module from a flow_by_phase_chart artifact.
 * When multiple boards are present, returns one link per board.
 *
 * @param {Record<string, unknown>} _data - The artifact data payload containing work_periods array
 * @param {Record<string, unknown>} config - The artifact config containing boards array
 * @returns {QuickLink[]} Array of quick links (one per board, or empty)
 */
function buildFlowByPhaseQuickLink(
  _data: Record<string, unknown>,
  config: Record<string, unknown> | undefined,
): QuickLink[] {
  const boards = getBoardEntries(config);
  if (boards.length === 0) return [];

  const startDate = config?.start_date as string | undefined;
  const endDate = config?.end_date as string | undefined;
  const isSingleBoard = boards.length === 1;

  return boards.map((board) => {
    const params = new URLSearchParams();
    params.set('workPeriodBoardId', board.id);
    params.set('selectedTab', 'work_phase_distribution');

    if (startDate) {
      params.set('startDate', startDate);
    }
    if (endDate) {
      params.set('endDate', endDate);
    }
    if (board.work_period_ids && board.work_period_ids.length > 0) {
      params.set('selectedWorkPeriodIds', board.work_period_ids.join(','));
    }

    return {
      key: board.id,
      url: `${APPLICATION_BASE}/work-periods?${params.toString()}`,
      label: isSingleBoard ? 'Open in Work Periods' : `Open '${board.name}' in Work Periods`,
    };
  });
}

/**
 * Builds quick links for the Work Periods module from a work_pace_chart artifact.
 * When multiple boards are present, returns one link per board.
 *
 * @param {Record<string, unknown>} data - The artifact data payload containing work_periods array
 * @param {Record<string, unknown>} config - The artifact config containing boards array
 * @returns {QuickLink[]} Array of quick links (one per board, or empty)
 */
function buildWorkPaceQuickLink(
  data: Record<string, unknown>,
  config: Record<string, unknown> | undefined,
): QuickLink[] {
  const boards = getBoardEntries(config);
  if (boards.length === 0) return [];

  const workPeriod = getFirstWorkPeriod(data);
  const isSingleBoard = boards.length === 1;

  return boards.map((board) => {
    const params = new URLSearchParams();
    params.set('workPeriodBoardId', board.id);
    params.set('selectedTab', 'work_pace');

    if (workPeriod?.start_date) {
      params.set('startDate', workPeriod.start_date);
    }
    if (workPeriod?.end_date) {
      params.set('endDate', workPeriod.end_date);
    }

    const boardWorkPeriodIds = board.work_period_ids ?? [];
    const selectedWorkPeriodIds =
      workPeriod?.id && boardWorkPeriodIds.includes(workPeriod.id)
        ? [workPeriod.id]
        : boardWorkPeriodIds.length > 0
          ? boardWorkPeriodIds
          : workPeriod?.id
            ? [workPeriod.id]
            : [];

    if (selectedWorkPeriodIds.length > 0) {
      params.set('selectedWorkPeriodIds', selectedWorkPeriodIds.join(','));
    }

    return {
      key: board.id,
      url: `${APPLICATION_BASE}/work-periods?${params.toString()}`,
      label: isSingleBoard ? 'Open in Work Periods' : `Open '${board.name}' in Work Periods`,
    };
  });
}

/**
 * Returns quick links for the given artifact type, data, and config.
 * This is the main entry point for computing quick links from Flora artifacts.
 *
 * @param {ArtifactType} artifactType - The type of artifact
 * @param {Record<string, unknown>} data - The artifact data payload
 * @param {Record<string, unknown>} config - The artifact config
 * @returns {QuickLink[]} Array of quick links (empty if the artifact type is unsupported or has insufficient context)
 */
function getQuickLinkForArtifact(
  artifactType: ArtifactType,
  data: Record<string, unknown>,
  config?: Record<string, unknown>,
): QuickLink[] {
  switch (artifactType) {
    case 'process_map_chart':
      return buildProcessQuickLink(data, config);
    case 'measure_comparison_chart':
      return buildMeasureComparisonQuickLink(data, config);
    case 'flow_by_phase_chart':
      return buildFlowByPhaseQuickLink(data, config);
    case 'measures_over_time_chart':
      return buildMeasuresOverTimeQuickLink(data, config);
    case 'work_pace_chart':
      return buildWorkPaceQuickLink(data, config);
    default:
      return [];
  }
}

export {
  buildFlowByPhaseQuickLink,
  buildMeasureComparisonQuickLink,
  buildMeasuresOverTimeQuickLink,
  buildProcessQuickLink,
  buildWorkPaceQuickLink,
  getQuickLinkForArtifact,
  isSprintLevel,
};
